/**
 * The Valve Isolation Trace's own printable summary.
 *
 * A plain, text-only sheet — unlike utils/printSheet.ts, there is no map
 * image here, so that file's legend/scale-bar/neat-line machinery would be
 * dead weight. Produces the same { canvas, cssPageSize } shape printSheet.ts
 * itself returns, so its existing `printSheet()` popup-and-print path (the
 * app's one "Save as PDF" mechanism — there is no PDF library here) and
 * `downloadSheet()` PNG path both work on it unmodified.
 */

const DPI = 150;
const MM_PER_INCH = 25.4;
const [PAGE_W_MM, PAGE_H_MM] = [210, 297]; // A4 portrait
const MARGIN_MM = 16;

const mmToPx = (mm: number) => Math.round((mm / MM_PER_INCH) * DPI);

export interface IsolationReportRow {
  label: string;
  layerName: string;
}

export interface IsolationReportData {
  title: string;
  generatedAt: Date;
  startCount: number;
  toleranceMetres: number;
  maxEdges: number;
  valveLayerName: string | null;
  valveTypeField: string | null;
  valveTypeValues: string[];
  isolatedLength: string;
  segmentCount: number;
  valveCount: number;
  assetCount: number;
  openEndCount: number;
  truncated: boolean;
  byLayer: { layerName: string; lines: number; assets: number; valves: number }[];
  valveRows: IsolationReportRow[];
  openEnds: [number, number][];
}

export interface IsolationReportSheet {
  canvas: HTMLCanvasElement;
  widthPx: number;
  heightPx: number;
  cssPageSize: string;
}

/** Wraps text to a pixel width, breaking on spaces — used for the one field
 *  (the valve-type filter line) long enough to possibly need it. */
function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** A simple bordered table: a header row, then one row per entry — the only
 *  table shape this report needs. Returns the Y position just below it. */
function drawTable(
  context: CanvasRenderingContext2D,
  x: number,
  startY: number,
  width: number,
  columns: { label: string; weight: number }[],
  rows: string[][],
  emptyLabel: string,
): number {
  const rowHeight = mmToPx(6.4);
  const headerHeight = mmToPx(6.4);
  const pad = mmToPx(2.2);
  const totalWeight = columns.reduce((sum, column) => sum + column.weight, 0);
  const widths = columns.map((column) => (column.weight / totalWeight) * width);

  let y = startY;
  context.font = `700 ${mmToPx(3)}px Inter, system-ui, sans-serif`;
  context.fillStyle = '#eef2f7';
  context.fillRect(x, y, width, headerHeight);
  context.strokeStyle = '#cbd5e1';
  context.lineWidth = mmToPx(0.25);
  context.strokeRect(x, y, width, headerHeight);

  let columnX = x;
  context.fillStyle = '#0f2942';
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  columns.forEach((column, index) => {
    context.fillText(column.label, columnX + pad, y + headerHeight / 2);
    columnX += widths[index];
  });
  y += headerHeight;

  if (rows.length === 0) {
    context.font = `400 ${mmToPx(3)}px Inter, system-ui, sans-serif`;
    context.fillStyle = '#94a3b8';
    context.strokeRect(x, y, width, rowHeight);
    context.fillText(emptyLabel, x + pad, y + rowHeight / 2);
    return y + rowHeight;
  }

  context.font = `400 ${mmToPx(3)}px Inter, system-ui, sans-serif`;
  rows.forEach((row, rowIndex) => {
    context.fillStyle = rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
    context.fillRect(x, y, width, rowHeight);
    context.strokeStyle = '#e2e8f0';
    context.lineWidth = mmToPx(0.2);
    context.strokeRect(x, y, width, rowHeight);

    columnX = x;
    context.fillStyle = '#334155';
    row.forEach((cell, columnIndex) => {
      const cellWidth = widths[columnIndex] - pad * 2;
      let text = cell;
      if (context.measureText(text).width > cellWidth) {
        while (text.length > 1 && context.measureText(`${text}…`).width > cellWidth) {
          text = text.slice(0, -1);
        }
        text = `${text}…`;
      }
      context.fillText(text, columnX + pad, y + rowHeight / 2);
      columnX += widths[columnIndex];
    });
    y += rowHeight;
  });

  return y;
}

export function renderIsolationReportSheet(data: IsolationReportData): IsolationReportSheet {
  const width = mmToPx(PAGE_W_MM);
  const height = mmToPx(PAGE_H_MM);
  const margin = mmToPx(MARGIN_MM);
  const contentWidth = width - margin * 2;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the report.');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);

  let y = margin;

  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.fillStyle = '#0f2942';
  context.font = `700 ${mmToPx(7)}px Inter, system-ui, sans-serif`;
  context.fillText(data.title, margin, y);
  y += mmToPx(9.5);

  context.font = `400 ${mmToPx(3.2)}px Inter, system-ui, sans-serif`;
  context.fillStyle = '#64748b';
  context.fillText(`Generated ${data.generatedAt.toLocaleString()}`, margin, y);
  y += mmToPx(7);

  context.strokeStyle = '#0f2942';
  context.lineWidth = mmToPx(0.5);
  context.beginPath();
  context.moveTo(margin, y);
  context.lineTo(width - margin, y);
  context.stroke();
  y += mmToPx(7);

  const heading = (text: string) => {
    context.font = `700 ${mmToPx(4.2)}px Inter, system-ui, sans-serif`;
    context.fillStyle = '#0f2942';
    context.fillText(text, margin, y);
    y += mmToPx(6.5);
  };
  const line = (text: string) => {
    context.font = `400 ${mmToPx(3.2)}px Inter, system-ui, sans-serif`;
    context.fillStyle = '#334155';
    for (const wrapped of wrapText(context, text, contentWidth)) {
      context.fillText(wrapped, margin, y);
      y += mmToPx(4.6);
    }
  };

  heading('Trace parameters');
  line(`Start flags: ${data.startCount}    Tolerance: ${data.toleranceMetres} m    Segment limit: ${data.maxEdges.toLocaleString()}`);
  line(`Valve layer: ${data.valveLayerName ?? '—'}`);
  if (data.valveTypeField) {
    line(`Valve filter: ${data.valveTypeField} = ${data.valveTypeValues.length > 0 ? data.valveTypeValues.join(', ') : 'any value'}`);
  }
  y += mmToPx(3);

  heading('Summary');
  line(`Isolated length: ${data.isolatedLength}`);
  line(`Segments traced: ${data.segmentCount}    Isolation valves: ${data.valveCount}    Assets on the run: ${data.assetCount}`);
  line(
    data.openEndCount > 0
      ? `Open ends: ${data.openEndCount} — NOT fully isolated. Flow can still escape there.`
      : 'Open ends: 0 — fully isolated.',
  );
  if (data.truncated) {
    line(`Warning: stopped at the ${data.maxEdges.toLocaleString()}-segment limit before reaching every bounding valve.`);
  }
  y += mmToPx(4);

  if (data.byLayer.length > 0) {
    heading('Isolated area, by layer');
    y = drawTable(
      context,
      margin,
      y,
      contentWidth,
      [
        { label: 'Layer', weight: 2.4 },
        { label: 'Segments', weight: 1 },
        { label: 'Assets', weight: 1 },
        { label: 'Valves', weight: 1 },
      ],
      data.byLayer.map((group) => [group.layerName, String(group.lines), String(group.assets), String(group.valves)]),
      'Nothing traced.',
    );
    y += mmToPx(6);
  }

  heading('Close these valves');
  y = drawTable(
    context,
    margin,
    y,
    contentWidth,
    [
      { label: 'Valve', weight: 2 },
      { label: 'Layer', weight: 1.4 },
    ],
    data.valveRows.map((row) => [row.label, row.layerName]),
    'No valve bounds this area.',
  );
  y += mmToPx(6);

  if (data.openEnds.length > 0) {
    heading('Open ends (no valve, nothing further connected)');
    y = drawTable(
      context,
      margin,
      y,
      contentWidth,
      [
        { label: 'Latitude', weight: 1 },
        { label: 'Longitude', weight: 1 },
      ],
      data.openEnds.map(([lng, lat]) => [lat.toFixed(6), lng.toFixed(6)]),
      '',
    );
  }

  context.font = `400 ${mmToPx(2.6)}px Inter, system-ui, sans-serif`;
  context.fillStyle = '#94a3b8';
  context.textAlign = 'right';
  context.textBaseline = 'bottom';
  context.fillText(
    'Geometric approximation, not a Utility Network trace — verify valve state in the field. © MapGeoid Pvt. Ltd.',
    width - margin,
    height - margin,
  );

  return { canvas, widthPx: width, heightPx: height, cssPageSize: 'A4 portrait' };
}
